import type { FC } from 'react';

import { Auth0Provider } from '@auth0/auth0-react';
import { StyledEngineProvider, ThemeProvider } from '@mui/material';
import { ThemeProvider as StylesThemeProvider } from '@mui/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { createBrowserHistory } from 'history';
import { Router } from 'react-router-dom';

import { ImpersonationBar } from 'layouts/2Pane/ImpersonationBar';
import { AuthProvider } from 'shared/contexts/Auth0Context';
import { FiltersContextProvider } from 'shared/contexts/FiltersContext';
import { SandboxContextProvider } from 'shared/contexts/SandboxContext';
import { TenancyContextProvider } from 'shared/contexts/TenancyContext';
import { CHUNK_AUTO_RECOVER_TAG, isChunkLoadError, reloadOnChunkError } from 'shared/lib/chunkError/reloadOnChunkError';
import { queryClient } from 'shared/lib/queryClient/queryClient';
import { captureSignupParams } from 'shared/lib/storage/captureSignupParams';
import { theme } from 'shared/theme';
import { ReactErrorView } from 'views/errors/ReactErrorView';

import { ChatIntegration } from './integrations/ChatIntegration';
import { KnockProvider } from './integrations/KnockProvider';
import { PostHogProvider } from './integrations/PostHog';
import { ScrollReset } from './integrations/ScrollReset';
import './integrations/sentry';

import { ModalProvider } from 'react-modal-hook';
import { TransitionGroup } from 'react-transition-group';

import { IncidentDialog } from 'shared/lib/statusPage/IncidentDialog';

import { SnackbarProvider } from './integrations/SnackbarProvider';
import { RoutesWrapper } from './routes/RoutesWrapper';
import { GlobalStyles } from './styles/GlobalStyles';

const history = createBrowserHistory();

captureSignupParams(window.location.search);

const App: FC = () => {
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <StylesThemeProvider theme={theme}>
          <Router history={history}>
            <Sentry.ErrorBoundary
              beforeCapture={(scope, error) => {
                if (isChunkLoadError(error)) {
                  scope.setTag(CHUNK_AUTO_RECOVER_TAG, 'true');
                }
              }}
              fallback={({ resetError }) => <ReactErrorView onReset={resetError} />}
              onError={(error) => reloadOnChunkError(error)}
            >
              <SnackbarProvider>
                <QueryClientProvider client={queryClient}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Auth0Provider
                      authorizationParams={{
                        redirect_uri: window.location.origin,
                      }}
                      clientId={import.meta.env.VITE_APP_AUTH0_CLIENT_ID ?? ''}
                      domain={import.meta.env.VITE_APP_AUTH0_DOMAIN ?? ''}
                      onRedirectCallback={(appState) => {
                        history.replace(appState?.backUrl || window.location.pathname);
                      }}
                    >
                      <AuthProvider>
                        <PostHogProvider>
                          <KnockProvider>
                            <ImpersonationBar />
                            <SandboxContextProvider>
                              <TenancyContextProvider>
                                <ModalProvider rootComponent={TransitionGroup}>
                                  <FiltersContextProvider>
                                    <GlobalStyles />
                                    <ScrollReset />
                                    <ChatIntegration />
                                    <RoutesWrapper />
                                    <IncidentDialog />
                                  </FiltersContextProvider>
                                </ModalProvider>
                              </TenancyContextProvider>
                            </SandboxContextProvider>
                          </KnockProvider>
                        </PostHogProvider>
                      </AuthProvider>
                    </Auth0Provider>
                  </LocalizationProvider>
                </QueryClientProvider>
              </SnackbarProvider>
            </Sentry.ErrorBoundary>
          </Router>
        </StylesThemeProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default App;
